import { z } from "zod";
import { CARD_DECK_IDS, DEFAULT_CARD_DECK } from "../domain/decks";
import { MATCH_RESULTS } from "../domain/games";
import { inviteUserSchema } from "./invites";

/**
 * Online-table settings contract — shared by the web Game Settings screen (which
 * renders the choices) and the game-server (which validates a host's payload with
 * {@link tableSettingsSchema} before applying it). Keeping the allowed values here
 * means the client can never offer an option the server would refuse.
 */

/**
 * The LEAK-FREE public identity of a human seated at an online table, carried on
 * every `lobby` roster broadcast so table-mates can render each other with the
 * real avatar + the shared `displayNameOf` name AND act on each other (friend
 * requests key on `userId`, the app cuid — the public id everything social uses).
 * Exactly the invite-party shape (`InviteUser`): public handle, names, avatar
 * refs. NEVER the Telegram id / Telegram username. Bots (and, defensively, a
 * human whose identity could not be resolved) simply omit it.
 */
export const tablePlayerIdentitySchema = inviteUserSchema;
export type TablePlayerIdentity = z.infer<typeof tablePlayerIdentitySchema>;

/** The ONLY per-turn timer choices a CARD online table may use, in seconds. */
/** The حکم match lengths a host may pick, and the canonical default a public table always uses. */
export const HOKM_MATCH_LENGTHS = [3, 5, 7] as const;
export type HokmMatchLength = (typeof HOKM_MATCH_LENGTHS)[number];
export const DEFAULT_HOKM_MATCH_LENGTH: HokmMatchLength = 7;

/**
 * کوت under a suit-less حکم call — the platform default, and what a PUBLIC table is forced to.
 * OFF: the reading the one publisher that states the scoring consequence gives, and what shipped
 * on 2026-08-25. A private host may turn it on. See `callPaysKot` in `@bing/engine-hokm`.
 *
 * ⚠️ The «اتاق عمومی پیشرفته» room is forced to this too, on purpose — it offers the three
 * suit-less calls and they pay no کوت there ({@link HOKM_ADVANCED_ONLINE_ROOM}).
 */
export const DEFAULT_HOKM_MODE_KOT = false;

/**
 * ARE THE THREE SUIT-LESS CALLS (سرس / نرس / آس نرس) OFFERED AT ALL? The platform default: **OFF**.
 *
 * The Hakem's seven-call vocabulary is a per-table setting, not a platform rule — a private host
 * turns it on in «تنظیمات میز», a solo player on the حکم start screen, and exactly one public room
 * ({@link HOKM_ADVANCED_ONLINE_ROOM}) forces it on. Everything else — the plain «اتاق عمومی», every
 * private table that never chose, every solo game — plays the four خال.
 *
 * Enforced in the ENGINE (`legalMoves`, and therefore `isLegal`), so a crafted «نرس» is unappliable
 * at a table that does not offer it. See `HokmState.modesAllowed` for the legacy-resume rule.
 */
export const DEFAULT_HOKM_MODES = false;

/**
 * «اتاق عمومی پیشرفته» — the ONE public حکم queue that plays the suit-less calls.
 *
 * A room's name IS its matchmaking queue (name + `maxClients` are fixed at `define()` time), so
 * «advanced» cannot be a table setting on a quick-match table: somebody who asked for the plain
 * room must never be seated at a table where the Hakem may call نرس. Two rooms, two queues.
 *
 * SHARED so the client can never ask for a room the server did not define — the failure that
 * mistake produces is a bare `4210 not defined`, which the web maps to nothing better than a
 * generic connection error.
 */
export const HOKM_ADVANCED_ONLINE_ROOM = "hokm-advanced-online";

/** The plain public حکم queue: the standard rules, and the name every old link resolves to. */
export const HOKM_ONLINE_ROOM = "hokm-online";

/**
 * شلم's MATCH TARGET — the points a team must reach to win (audit D13, 2026-10-09; card-67).
 *
 * A PRIVATE host — and a SOLO player, on the start screen — picks one of {@link SHELEM_TARGETS},
 * default {@link DEFAULT_SHELEM_TARGET} (۶۶۰ since card-67, the operator: «public tables 660 only
 * (the official rule); private tables 660 or 1200, default 660; solo selectable too, default 660»);
 * a PUBLIC quick-match has nobody to choose for the stranger who joins, so it is FORCED to
 * {@link PUBLIC_SHELEM_TARGET} from its room type; the dormant TOURNAMENT table, a private-mode room,
 * takes the private default (the operator, card-67). Points, not دست: a شلم hand is worth 165, so
 * ۶۶۰ is a short evening and ۱۲۰۰ the long match.
 *
 * ⚠️ A SECOND COPY OF THE ENGINE'S LIST. `@bing/shared` cannot import an engine (the web bundles it),
 * so {@link SHELEM_TARGETS} / {@link PUBLIC_SHELEM_TARGET} restate `TARGET_OPTIONS` / `PUBLIC_TARGET`
 * from `@bing/engine-shelem`; `apps/game-server/test/shelem-target.test.ts` pins that they agree.
 * The engine's own `DEFAULT_TARGET` (۱۲۰۰) is NOT this default and no room reaches it: every شلم room
 * — public, private (the tournament's too), solo — hands the engine its target explicitly.
 */
export const SHELEM_TARGETS = [660, 1200] as const;
export type ShelemTarget = (typeof SHELEM_TARGETS)[number];
export const DEFAULT_SHELEM_TARGET: ShelemTarget = 660;
export const PUBLIC_SHELEM_TARGET: ShelemTarget = 660;

/**
 * A chosen شلم target, VALIDATED: one of {@link SHELEM_TARGETS}, else {@link DEFAULT_SHELEM_TARGET}
 * (a cached older bundle sends none; a crafted client sends anything — a string, a fraction, 900).
 * ONE rule for a private host's pick (`TableController.normalizeSettings`) and a solo player's
 * (`SoloController`), so the two can never accept different sets.
 */
export function shelemTargetOrDefault(wanted: unknown): ShelemTarget {
  return (SHELEM_TARGETS as readonly unknown[]).includes(wanted)
    ? (wanted as ShelemTarget)
    : DEFAULT_SHELEM_TARGET;
}

/**
 * پاسور's MATCH TARGET — شلم's twin (the operator, card-pasur-cap): «public tables stay at 62; solo and
 * private tables choose 62, 100 or 104, with 62 as the default». A PRIVATE host and a SOLO player pick
 * one of {@link PASUR_TARGETS}, default {@link DEFAULT_PASUR_TARGET}; a PUBLIC quick-match and the
 * dormant TOURNAMENT table are FORCED to {@link PUBLIC_PASUR_TARGET} from their room type. The سور cap
 * follows from the target (`surCapOf` in `@bing/engine-pasur`: 50, 88, 92).
 *
 * ⚠️ A SECOND COPY OF ONE ENGINE VALUE: `@bing/shared` cannot import an engine (the web bundles it), so
 * the 62 here restates `DEFAULT_TARGET_SCORE`; `apps/game-server/test/pasur-target.test.ts` pins that
 * they agree.
 */
export const PASUR_TARGETS = [62, 100, 104] as const;
export type PasurTarget = (typeof PASUR_TARGETS)[number];
export const DEFAULT_PASUR_TARGET: PasurTarget = 62;
export const PUBLIC_PASUR_TARGET: PasurTarget = 62;

/**
 * A chosen پاسور target, VALIDATED: one of {@link PASUR_TARGETS}, else {@link DEFAULT_PASUR_TARGET}
 * (a cached older bundle sends none; a crafted client sends anything). ONE rule for a private host's
 * pick (`TableController.normalizeSettings`) and a solo player's (`SoloController`).
 */
export function pasurTargetOrDefault(wanted: unknown): PasurTarget {
  return (PASUR_TARGETS as readonly unknown[]).includes(wanted)
    ? (wanted as PasurTarget)
    : DEFAULT_PASUR_TARGET;
}

export const TURN_TIMER_OPTIONS = [10, 15, 20, 25, 30] as const;
export type TurnTimerSec = (typeof TURN_TIMER_OPTIONS)[number];

/**
 * The default per-turn timer for a PRIVATE table — preselected in the UI and the schema default.
 *
 * PUBLIC tables do not use this at all; they run {@link PUBLIC_TURN_TIMER_SEC}. This is the value a
 * private host starts from and may change to anything in {@link TURN_TIMER_OPTIONS}.
 */
export const DEFAULT_TURN_TIMER_SEC: TurnTimerSec = 15;

/**
 * EVERY PUBLIC (quick-match) TABLE RUNS A 10-SECOND TURN, whatever the game.
 *
 * A public table seats strangers who did not agree on a pace, and the cost of a slow player is
 * paid by three other people who cannot leave without abandoning the hand. A private table is the
 * opposite — friends who chose each other — so it keeps the host's pick from
 * {@link TURN_TIMER_OPTIONS}.
 *
 * Deliberately ONE constant rather than a per-game lookup: 10 is a member of BOTH
 * {@link TURN_TIMER_OPTIONS} and {@link MENSCH_TURN_TIMER_OPTIONS}, so it survives
 * `TableController.normalizeSettings`'s per-game clamp unchanged. If either set ever loses 10,
 * `turn-timer.test.ts` goes red rather than a public table silently falling back to its game
 * default.
 */
export const PUBLIC_TURN_TIMER_SEC = 10;

export function isTurnTimerSec(value: unknown): value is TurnTimerSec {
  return typeof value === "number" && (TURN_TIMER_OPTIONS as readonly number[]).includes(value);
}

/**
 * ITEM 7 — منچ runs on a MUCH shorter clock than a card game, and needs its own set.
 *
 * A منچ turn is one tap: roll, then (at most) pick which token moves. There is no hand to read
 * and no trick to think about, so the card set starting at 15s leaves everyone waiting on
 * somebody who has already decided. Five to twenty-five seconds fits the actual decision.
 */
export const MENSCH_TURN_TIMER_OPTIONS = [5, 10, 15, 20, 25] as const;
export type MenschTurnTimerSec = (typeof MENSCH_TURN_TIMER_OPTIONS)[number];

/** The default منچ per-turn timer. */
export const DEFAULT_MENSCH_TURN_TIMER_SEC: MenschTurnTimerSec = 10;

/**
 * مار پله's own set (card-86 review, 2026-11-03): منچ's WITHOUT the 5 s window — and nothing else
 * changes (منچ keeps 5/10/15/20/25). 10 stays a member ({@link PUBLIC_TURN_TIMER_SEC}).
 *
 * RE-DERIVED FOR ROUND 2's TWO PHASES (roll, then move — منچ's mechanism: EACH phase gets the FULL
 * window, armed on the state change). The worst motion in front of a window: before a ROLL, the
 * last move's walk + longest slide + the hit piece sliding home + settle (`moveBeatMs` ≤ 3.5 s on
 * the 18 maps); before a MOVE, the die's tumble + read (`rollHoldMs` 1.35 s). At 5 s a roll window
 * would leave 1.5 s after the longest motion — so 5 stays out; at 10 s ≥ 6.5 s remain before a roll
 * and ≥ 8.65 s before a move. `normalizeSettings` clamps a crafted 5 on a ladders table to the default, and the
 * private-table screen never offers it.
 */
export const LADDERS_TURN_TIMER_OPTIONS = [10, 15, 20, 25] as const;
export type LaddersTurnTimerSec = (typeof LADDERS_TURN_TIMER_OPTIONS)[number];

/** The default مار پله per-turn timer (a private table's; a public one runs {@link PUBLIC_TURN_TIMER_SEC}). */
export const DEFAULT_LADDERS_TURN_TIMER_SEC: LaddersTurnTimerSec = 10;

/**
 * مار پله ROUND 2 (card-86, 2026-11-03) — the LEVEL and the LOOK, table properties.
 *
 * ⚠️ A COPY of `LADDERS_LEVELS` / `LADDERS_STYLES` in `@bing/engine-ladders` (the source of truth
 * — this package depends on zod alone, never on an engine); `apps/game-server/test/
 * ladders-contract.test.ts` pins the copies equal, order included. Which LOOKS a build can draw
 * (`LADDERS_LIVE_STYLES`: `bg2` only once its picture ships) is the ENGINE's list: this schema
 * accepts every id, and the server clamps.
 */
export const LADDERS_LEVEL_IDS = ["easy", "medium", "hard"] as const;
export const LADDERS_STYLE_IDS = ["jungle", "bg2", "classic"] as const;

/**
 * تخته نرد's own set — a HOST-SELECTABLE per-turn window (operator's call, 2026-08-15).
 *
 * It replaces the fixed 45s of the first cut. 45 was chosen as "the reference's own generous
 * window", but a backgammon turn on a phone is a throw plus a couple of taps, and 45 seconds of
 * an opponent's silence is a long time to sit through — most of all on the §5.1 opening contest,
 * where the window is SHARED and the board shows nothing at all until somebody throws.
 *
 * ⚠️ THE WINDOW IS ALSO THE IDLE RULE'S CLOCK. Shortening it shortens the runway before a seat is
 * handed to the bot, so {@link BACKGAMMON_IDLE_MISS_LIMIT} moves with it — see that constant.
 *
 * ⚠️ THE SET IS 15/20/25/30 (operator, 2026-08-15), not the earlier 10/15/20/25. Ten seconds was
 * short for a turn that is a throw plus up to four taps, so the floor moved up a step and the set
 * gained 30 at the top. A cached client still asking for 10 is CLAMPED to the fallback by
 * `TableController.normalizeSettings` rather than refused, so a stray value never blocks a start.
 */
export const BACKGAMMON_TURN_TIMER_OPTIONS = [15, 20, 25, 30] as const;
export type BackgammonTurnTimerSec = (typeof BACKGAMMON_TURN_TIMER_OPTIONS)[number];

/** The default تخته نرد per-turn timer — a PRIVATE host's starting point, and the fallback for
 *  anything off {@link BACKGAMMON_TURN_TIMER_OPTIONS}. A PUBLIC table runs
 *  {@link PUBLIC_BACKGAMMON_TURN_TIMER_SEC}, which happens to be the same 20 today but is a
 *  SEPARATE decision: a host may move theirs, and a quick-match table's is forced. */
export const DEFAULT_BACKGAMMON_TURN_TIMER_SEC: BackgammonTurnTimerSec = 20;

/**
 * EVERY PUBLIC تخته نرد TABLE RUNS A 20-SECOND TURN (operator, 2026-08-15).
 *
 * Separate from {@link PUBLIC_TURN_TIMER_SEC} (10, the card games') because a backgammon turn is a
 * throw plus up to four taps, not one card — and separate from
 * {@link DEFAULT_BACKGAMMON_TURN_TIMER_SEC} (15) because that one is a private host's own choice,
 * which this must not move.
 *
 * ⚠️ IT IS ALSO THE IDLE RULE'S CLOCK: with {@link BACKGAMMON_IDLE_MISS_LIMIT} = 3 a silent seat is
 * handed to the bot after 3×20s = 60s. Change either and re-reason the pair.
 *
 * A member of {@link BACKGAMMON_TURN_TIMER_OPTIONS}, so it survives the per-game clamp in
 * `TableController.normalizeSettings` unchanged.
 */
export const PUBLIC_BACKGAMMON_TURN_TIMER_SEC: BackgammonTurnTimerSec = 20;

/* ------------------------------------------------------------------ *
 * دوز (Connect Four) — its own timer set, its own match lengths.
 * ------------------------------------------------------------------ */

/**
 * دوز's per-turn window, host-selectable on a PRIVATE table (operator, 2026-09-03: «Private: host
 * picks 15 / 20 / 30, default 20»).
 *
 * A دوز turn is ONE tap on a column — no hand to read, no dice to throw, no sub-moves to stage —
 * but the decision is a real one, so it sits between منچ's 5-to-25 (a roll and at most four
 * tokens) and the card set's 10-to-30.
 *
 * ⚠️ 15 IS A MEMBER OF THIS SET ON PURPOSE. `TableController.normalizeSettings` clamps a table's
 * timer to its own game's options, and a public دوز table is FORCED to
 * {@link PUBLIC_DOOZ_TURN_TIMER_SEC}; if 15 ever left this list that forced value would be
 * silently rewritten to the default and every public table would run a 20-second turn.
 */
export const DOOZ_TURN_TIMER_OPTIONS = [15, 20, 30] as const;
export type DoozTurnTimerSec = (typeof DOOZ_TURN_TIMER_OPTIONS)[number];

/**
 * The private host's starting point, and the fallback for anything off the list.
 *
 * ⚠️ FIFTEEN, THE SAME AS PUBLIC (operator, 2026-09-03 — it was 20). A دوز turn is one tap on a
 * column; twenty seconds of it is dead time on a two-seat table where the other player has
 * nothing to do but wait. The host can still choose 20 or 30 — this is only where the picker
 * starts.
 */
export const DEFAULT_DOOZ_TURN_TIMER_SEC: DoozTurnTimerSec = 15;

/**
 * EVERY PUBLIC دوز TABLE RUNS A 15-SECOND TURN (operator, 2026-09-03).
 *
 * Kept as its own constant even though it now equals {@link DEFAULT_DOOZ_TURN_TIMER_SEC}: this
 * one is FORCED and that one is merely a starting point a private host may move off. Collapsing
 * them would make a later change to either silently change the other.
 *
 * ⚠️ IT IS ALSO THE IDLE RULE'S CLOCK. دوز takes the platform default
 * ({@link PUBLIC_IDLE_MISS_LIMIT} = 5, in the game-server), so a silent PUBLIC seat is handed to a
 * bot after 5 × 15 s = 75 s. Change either and re-reason the pair.
 */
export const PUBLIC_DOOZ_TURN_TIMER_SEC: DoozTurnTimerSec = 15;

/**
 * How many دست a دوز match runs — EXACTLY, not «first to».
 *
 * A public table is always {@link PUBLIC_DOOZ_MATCH_LENGTH} (there is nobody to choose for the
 * stranger who joins); a private host picks either. Solo asks on the start screen.
 */
export const DOOZ_MATCH_LENGTHS = [3, 5] as const;
export type DoozMatchLength = (typeof DOOZ_MATCH_LENGTHS)[number];

/** The private host's starting point — and the fallback for anything off the list. */
export const DEFAULT_DOOZ_MATCH_LENGTH: DoozMatchLength = 3;

/**
 * THE TWO PIECE COLOURS — the classic Connect Four pair, and there are exactly two.
 *
 * ⚠️ THE COLOUR BELONGS TO A SEAT, NOT TO A SCREEN (operator, 2026-09-03). It used to be a purely
 * viewer-side preference with FOUR options: each player rendered their own pieces in whatever they
 * had picked and the opponent's in the contrasting one, so two players could both be looking at a
 * red disc in the same cell and a screenshot passed between them disagreed. It is a shared fact
 * now, exactly like شطرنج's colours: this constant names the colour of SEAT 0, the other seat
 * takes the remaining one, and both screens draw the same board.
 *
 * Public: seat 0 — whoever created the table — takes {@link DEFAULT_DOOZ_SEAT0_COLOR} and there is
 * nothing to ask. Private: the HOST picks (they are seat 0) and their opponent takes the other.
 * Solo: the human is seat 0 and picks on the start screen.
 */
export const DOOZ_PIECE_COLORS = ["red", "yellow"] as const;
export type DoozPieceColor = (typeof DOOZ_PIECE_COLORS)[number];

/** The classic Connect Four red — what seat 0 takes unless a host says otherwise. */
export const DEFAULT_DOOZ_SEAT0_COLOR: DoozPieceColor = "red";

/** The other colour. دوز is exactly two seats and exactly two colours, so this is total. */
export function otherDoozColor(color: DoozPieceColor): DoozPieceColor {
  return color === "red" ? "yellow" : "red";
}

/**
 * WHAT `seat` PLAYS IN, given the colour seat 0 holds — the ONE definition, so the two screens,
 * the swatch beside each name and the discs on the board can never disagree.
 */
export function doozSeatColor(seat: number, seatZero: DoozPieceColor): DoozPieceColor {
  return seat === 0 ? seatZero : otherDoozColor(seatZero);
}

/** «Public room: always 3 hands, no picker» (operator, 2026-09-03). Forced by
 *  `TableController.normalizeSettings` over anything a crafted client sends. */
export const PUBLIC_DOOZ_MATCH_LENGTH: DoozMatchLength = 3;

/** The single public دوز queue. SHARED so the client can never ask for a room the server did not
 *  define — that failure surfaces as a bare `4210 not defined`, which the web can only render as a
 *  generic connection error. */
export const DOOZ_ONLINE_ROOM = "dooz-online";

/**
 * مار پله's ROOM TYPES (card-86) — one per SIZE and DOOR, because a room's `maxClients` is fixed at
 * `define()` time: the پاسور pattern (`pasur2-online`), not منچ's single four-seat public queue.
 * Somebody who tapped the «۲ نفره» public button must never be seated at a table for four.
 * SHARED so the game-server's `define()` and the web's join can never disagree on a name (that
 * failure is a bare `4210 not defined`). The solo room is the game id itself, like every game's.
 */
export const LADDERS_SOLO_ROOM = "ladders";
export function laddersOnlineRoom(seats: number): string {
  return seats === 2 ? "ladders2-online" : "ladders-online";
}
export function laddersPrivateRoom(seats: number): string {
  return seats === 2 ? "ladders2-private" : "ladders-private";
}

/**
 * The turn-timer choices a given game may use, and its default — the single source both the
 * Game Settings screen and the server's per-game clamp read, so the client can never offer a
 * value the server would rewrite.
 */
export function turnTimerChoicesFor(game: string): {
  options: readonly number[];
  fallback: number;
} {
  if (game === "mensch")
    return { options: MENSCH_TURN_TIMER_OPTIONS, fallback: DEFAULT_MENSCH_TURN_TIMER_SEC };
  // مار پله: منچ's short clock per PHASE (roll, then move), minus its 5 s (round 2's re-derivation).
  if (game === "ladders")
    return { options: LADDERS_TURN_TIMER_OPTIONS, fallback: DEFAULT_LADDERS_TURN_TIMER_SEC };
  if (game === "backgammon")
    return {
      options: BACKGAMMON_TURN_TIMER_OPTIONS,
      fallback: DEFAULT_BACKGAMMON_TURN_TIMER_SEC,
    };
  if (game === "dooz")
    return { options: DOOZ_TURN_TIMER_OPTIONS, fallback: DEFAULT_DOOZ_TURN_TIMER_SEC };
  return { options: TURN_TIMER_OPTIONS, fallback: DEFAULT_TURN_TIMER_SEC };
}

/* ------------------------------------------------------------------ *
 * CHESS — a chess-only settings block riding on {@link tableSettingsSchema} as an
 * OPTIONAL sub-object. Card tables never set it (→ `undefined`), so their contract
 * is byte-for-byte unchanged; only chess tables carry it. The block records the
 * host's up-front choices (which colour they take, the time control, and how a
 * disconnect is handled). Enforcement of these lives in later chunks; for now the
 * block is stored + surfaced (Game Settings pickers + read-only waiting-room chips).
 * ------------------------------------------------------------------ */

/** Which side the host plays. `random` = decided at seating (the default). */
export const CHESS_COLORS = ["white", "black", "random"] as const;
export type ChessColor = (typeof CHESS_COLORS)[number];

/** Fixed time-control presets. `none` = untimed (the default). initial+increment in seconds. */
export const CHESS_TIME_CONTROLS = {
  none: null,
  "3+2": { initialSec: 180, incrementSec: 2 },
  "5+0": { initialSec: 300, incrementSec: 0 },
  "10+0": { initialSec: 600, incrementSec: 0 },
  "15+10": { initialSec: 900, incrementSec: 10 },
} as const;
export type ChessTimeControl = keyof typeof CHESS_TIME_CONTROLS;

/** How a mid-game disconnect resolves — `claim` a win after a grace, hand the seat to a
 *  `bot`, or `pause` the table. Each is built + tested in a later chunk; `claim` is the default. */
export const CHESS_DISCONNECT_POLICIES = ["claim", "bot", "pause"] as const;
export type ChessDisconnectPolicy = (typeof CHESS_DISCONNECT_POLICIES)[number];

export const chessSettingsSchema = z.object({
  color: z.enum(CHESS_COLORS).default("random"),
  // Timed by default — see DEFAULT_CHESS_SETTINGS for why. The two must agree: this is what a
  // client sending a partial block gets, that is what the settings screen seeds.
  timeControl: z.enum(["none", "3+2", "5+0", "10+0", "15+10"]).default("10+0"),
  // Default «توقف» (pause): a dropped opponent freezes the board+clock and the present player waits
  // (then may claim), rather than instantly claiming — the gentlest default for a real disconnect.
  disconnectPolicy: z.enum(CHESS_DISCONNECT_POLICIES).default("pause"),
});
export type ChessSettings = z.infer<typeof chessSettingsSchema>;

/** The pre-selected chess block a host starts from (all three defaults). Used by the
 *  web Game Settings screen to seed a chess table's settings state. */
export const DEFAULT_CHESS_SETTINGS: ChessSettings = {
  color: "random",
  // ONLINE CHESS IS TIMED BY DEFAULT (10 minutes each, no increment).
  //
  // Chess deliberately never arms the card per-turn timer — a forced 25-second auto-move is a
  // blunder, so `armTurnTimer` skips chess entirely and a TIMED table arms the flag-fall clock
  // instead. With this defaulting to «آزاد» that meant an online chess game had no clock at all,
  // and a QUICK-MATCH table has no host to change it, so there was never a timer on screen. A
  // host who wants an untimed game still picks «آزاد»; nobody has to pick anything to get one.
  timeControl: "10+0",
  disconnectPolicy: "pause",
};

/* ------------------------------------------------------------------ *
 * BACKGAMMON — a backgammon-only settings block riding on {@link tableSettingsSchema} as an
 * OPTIONAL sub-object (mirrors the chess block). Card + chess tables never set it → their contracts
 * are byte-for-byte unchanged. `disconnectPolicy` REUSES the chess policy set (spec §8.1/§10).
 * ------------------------------------------------------------------ */

/** Which visual side the host takes (the opening roll still decides who actually moves first, §5.1). */
export const BACKGAMMON_SIDES = ["seat0", "seat1", "random"] as const;
export type BackgammonSide = (typeof BACKGAMMON_SIDES)[number];

/**
 * A تخته نرد match runs to a POINT total: a plain win pays 1, مارس 2 and سگ‌مارس 3, so «۵» means
 * first to five points — one سگ‌مارس takes you most of the way. `1` is a single دست.
 */
export const BACKGAMMON_MATCH_LENGTHS = [1, 3, 5, 7] as const;
/** «۵ دست» is the conventional length of a traditional match. */
export const DEFAULT_BACKGAMMON_MATCH_LENGTH = 5;
export type BackgammonMatchLength = (typeof BACKGAMMON_MATCH_LENGTHS)[number];

/*
 * ⚠️ THERE IS NO WHOLE-GAME CLOCK IN تخته نرد, AND THERE MUST NOT BE ONE (operator, 2026-08-15).
 *
 * A `timeControl` block used to ride here, mirroring chess: a chess-style time bank with a
 * flag-fall. It was removed outright — a backgammon turn is a throw the player does not choose and
 * a couple of taps, so a whole-game budget measures luck as much as thinking, and the table already
 * carries the thing that actually protects it from a stalled seat: the PER-TURN window
 * ({@link BACKGAMMON_TURN_TIMER_OPTIONS}), which is also the idle rule's clock.
 *
 * A cached client that still sends `timeControl` is harmless: this schema STRIPS unknown keys
 * (zod's default), so the rest of its settings still parse — the documented parsed-then-clamped
 * landing this file relies on everywhere else.
 */
export const backgammonSettingsSchema = z.object({
  side: z.enum(BACKGAMMON_SIDES).default("random"),
  /** Points, NOT rounds — مارس pays 2 and سگ‌مارس 3 toward the target. 5 is the traditional length. */
  matchLength: z
    .union([z.literal(1), z.literal(3), z.literal(5), z.literal(7)])
    .default(DEFAULT_BACKGAMMON_MATCH_LENGTH),
  doublingCube: z.boolean().default(false), // OUT of v1 default (§5.7); flag only
  disconnectPolicy: z.enum(CHESS_DISCONNECT_POLICIES).default("pause"),
});
export type BackgammonSettings = z.infer<typeof backgammonSettingsSchema>;

/** The SHORT public تخته نرد match — «۳ دست», the quicker of the two public queues. */
export const BACKGAMMON_SHORT_MATCH_LENGTH = 3;

/**
 * PUBLIC (quick-match) تخته نرد runs one of these, and ONLY these — one room type each.
 *
 * ⚠️ تخته نرد IS THE DELIBERATE EXCEPTION to «a public table offers no choices at all» (operator,
 * 2026-08-27). Every other game's public rooms differ only where the RULES differ (حکم's two call
 * vocabularies); this one splits by LENGTH, because a three-point match and a five-point match are
 * different amounts of somebody's evening and a stranger cannot be asked to agree to either.
 *
 * It has been both ways: `[3, 5]` originally, `[5]` for one day on 2026-08-26, `[3, 5]` again now.
 * The cost is real and was MEASURED before it was taken — splitting the pool roughly halves each
 * queue's chance of finding a human inside the 45s fill window — and it is the operator's call.
 *
 * Kept as a SET rather than inlined because `normalizeSettings` clamps `publicMatchTo` against it
 * and falls back to the default — the shape that made the 3-point queue's ordering bug catchable.
 * Widening it is one entry here plus one `define()`, which is the honest cost of adding a public
 * queue: it SPLITS the matchmaking pool, so it must be a decision, never a side effect.
 */
export const BACKGAMMON_PUBLIC_MATCH_LENGTHS = [
  BACKGAMMON_SHORT_MATCH_LENGTH,
  DEFAULT_BACKGAMMON_MATCH_LENGTH,
] as const;

/**
 * THE TWO PUBLIC تخته نرد QUEUES, by name.
 *
 * A room's name IS its matchmaking queue (name + `maxClients` are fixed at `define()` time), so
 * «۳ or ۵ دست» cannot be a table setting on a quick-match table — somebody who asked for the short
 * match must never be seated at a five-point one. Two rooms, two queues, exactly as حکم splits its
 * two call vocabularies.
 *
 * SHARED so the client can never ask for a room the server did not define — the failure that
 * mistake produces is a bare `4210 not defined`, which the web maps to nothing better than a
 * generic connection error.
 *
 * ⚠️ THE SHORT ROOM KEEPS ITS ORIGINAL NAME, `backgammon3-online`. It has been registered
 * continuously since the queues first shipped (as a RETIRED alias playing the standard length
 * while the split was off), so a cached bundle older than this one already asks for it — and now
 * gets exactly the short match its user originally picked.
 */
export const BACKGAMMON_SHORT_ONLINE_ROOM = "backgammon3-online";

/** The standard public تخته نرد queue: five points, and the name every old link resolves to. */
export const BACKGAMMON_ONLINE_ROOM = "backgammon-online";

/** The pre-selected backgammon block a host starts from (all defaults) — seeds Game Settings. */
export const DEFAULT_BACKGAMMON_SETTINGS: BackgammonSettings = {
  side: "random",
  matchLength: DEFAULT_BACKGAMMON_MATCH_LENGTH,
  doublingCube: false,
  disconnectPolicy: "pause",
};

/**
 * A host's table-rules payload (`client.create(...)` options / the pre-start
 * `"settings"` message) — the THREE choices a private-table host may make:
 *
 *  - `turnTimerSec` — one of {@link TURN_TIMER_OPTIONS} (anything else fails the
 *    parse and the server keeps its current settings), defaulting to
 *    {@link DEFAULT_TURN_TIMER_SEC}. There is no "no timer" option: every online
 *    table runs a turn clock.
 *  - `muted` — the OWNER mutes the whole table: all game sound is off for every
 *    seated player. Default `false` (sound on).
 *  - `chatAllowed` — whether the table chat is available. Default `true`.
 *  - `deck` — the card-back skin every seated player sees. Default `standard`.
 *
 * Unknown keys are STRIPPED (zod's default), so an older client that still sends
 * the retired `abandonPolicy`/`botFill` is tolerated during a deploy window —
 * those are ignored and the table uses the server's internal drop/fill defaults.
 *
 * `chess` is an OPTIONAL chess-only sub-block (see {@link chessSettingsSchema}). A
 * card table never sends it, so its parsed settings carry no `chess` key at all —
 * the card contract is completely unchanged; only a chess table populates it. The
 * `backgammon` sub-block (see {@link backgammonSettingsSchema}) works identically —
 * only a backgammon table sets it; card + chess tables carry no `backgammon` key.
 */
export const tableSettingsSchema = z.object({
  // The union spans EVERY game's choices (منچ's short 5..25 as well as the card 15..60); the
  // server then clamps to the calling game's own set from the trusted room game — see
  // `TableController.normalizeSettings`. A crafted client cannot give a card table a 5s turn.
  turnTimerSec: z
    .union([
      z.literal(5),
      z.literal(10),
      z.literal(15),
      z.literal(20),
      z.literal(25),
      z.literal(30),
      // 35/45/60 are RETIRED from TURN_TIMER_OPTIONS but stay ACCEPTED here on purpose. A client
      // that still offers them (a cached bundle during a deploy window) would otherwise fail the
      // whole parse, and `sanitizeSettings` returning null costs the host EVERY other setting they
      // chose — mute, chat, deck. Parsed and then clamped to the game's default by
      // `normalizeSettings` is a far gentler landing than silently discarding the lot.
      z.literal(35),
      z.literal(45),
      z.literal(60),
    ])
    .default(DEFAULT_TURN_TIMER_SEC),
  /**
   * حکم AND دوز ONLY — the match length, in each game's own units.
   *
   *   • حکم: how many ROUNDS win the match (3 / 5 / 7). A PUBLIC quick-match table ignores it and
   *     always plays the canonical 7.
   *   • دوز: how many دست the match RUNS, exactly (3 / 5 — see {@link DOOZ_MATCH_LENGTHS}; a 7
   *     from a crafted or cached client is clamped to دوز's default rather than refused). A PUBLIC
   *     table is forced to {@link PUBLIC_DOOZ_MATCH_LENGTH}.
   *
   * OPTIONAL, and stripped by `TableController.normalizeSettings` for every OTHER game, judged by
   * the TRUSTED room game — so a crafted client can never shorten a بی‌دل or شلم match.
   */
  matchLength: z.union([z.literal(3), z.literal(5), z.literal(7)]).optional(),
  /**
   * حکم ONLY — does a 7–0 sweep pay a کوت under one of the three SUIT-LESS calls (سرس / نرس /
   * آس نرس)? A suit call always pays and ignores this.
   *
   * A HOST setting because the sources treat it as a pre-game agreement rather than a rule (see
   * `callPaysKot` in `@bing/engine-hokm` for the full ledger). DEFAULT OFF — the conventional
   * reading, and what shipped on 2026-08-25 — so a table that never chooses is unchanged.
   *
   * OPTIONAL and stripped by `TableController.normalizeSettings` for any other game, judged by
   * the TRUSTED room game, so a crafted client cannot attach it to a پاسور table. A PUBLIC
   * quick-match table has no host to choose for the stranger who joins it, so it is FORCED to
   * the default there.
   */
  hokmModeKot: z.boolean().optional(),
  /**
   * دوز ONLY — the colour SEAT 0 plays in; the other seat takes the remaining one.
   *
   * A private host's choice (they are seat 0), so their opponent's board agrees with theirs
   * rather than each screen inventing its own palette. OPTIONAL and stripped by
   * `TableController.normalizeSettings` for every other game, judged by the TRUSTED room game; a
   * PUBLIC table has no host to choose for the stranger who joins, so it is FORCED to
   * {@link DEFAULT_DOOZ_SEAT0_COLOR}.
   */
  doozColor: z.enum(DOOZ_PIECE_COLORS).optional(),
  /**
   * حکم ONLY — may the Hakem call one of the three SUIT-LESS modes (سرس / نرس / آس نرس) at this
   * table at all? DEFAULT OFF ({@link DEFAULT_HOKM_MODES}); a private host turns it on.
   *
   * OPTIONAL and stripped by `TableController.normalizeSettings` for any other game, judged by the
   * TRUSTED room game, so a crafted client cannot attach a حکم-only rule to a پاسور table. A
   * PUBLIC quick-match table has no host to choose for the stranger who joins, so it is FORCED —
   * to OFF in the plain «اتاق عمومی» and to ON in «اتاق عمومی پیشرفته»
   * ({@link HOKM_ADVANCED_ONLINE_ROOM}), whichever way a crafted client asks.
   *
   * ⚠️ THE SETTING IS ONLY THE TABLE'S RECORD OF IT. What actually refuses a «نرس» is the ENGINE's
   * `legalMoves` (and therefore `isLegal`), which this reaches as the `modesAllowed` rule.
   */
  hokmModes: z.boolean().optional(),
  /**
   * شلم ONLY — the points that win the match: one of {@link SHELEM_TARGETS} (۶۶۰ / ۱۲۰۰). Anything
   * else fails the parse, so the client can never send a target the server would refuse.
   *
   * OPTIONAL with NO default — a default would put the key on every game's parsed payload — and
   * judged by the TRUSTED room game in `TableController.normalizeSettings`: STRIPPED for every
   * other game; on a PRIVATE شلم table DEFAULTED to {@link DEFAULT_SHELEM_TARGET} when absent (a
   * cached older bundle sends none) so the table publishes what it plays; on a PUBLIC one FORCED
   * from the room type ({@link PUBLIC_SHELEM_TARGET}), whichever way a crafted client asks. It
   * reaches the engine as the `targetScore` rule.
   */
  shelemTarget: z.union([z.literal(SHELEM_TARGETS[0]), z.literal(SHELEM_TARGETS[1])]).optional(),
  /**
   * پاسور ONLY — the points that win the match: one of {@link PASUR_TARGETS} (۶۲ / ۱۰۰ / ۱۰۴). The same
   * contract as {@link shelemTarget}: off the list fails the parse; OPTIONAL with NO default; judged by
   * the TRUSTED room game in `TableController.normalizeSettings`: STRIPPED for every other game,
   * DEFAULTED to {@link DEFAULT_PASUR_TARGET} on a private پاسور table that sent none, FORCED to
   * {@link PUBLIC_PASUR_TARGET} on a public or tournament one. It reaches the engine as the
   * `targetScore` rule.
   */
  pasurTarget: z
    .union([z.literal(PASUR_TARGETS[0]), z.literal(PASUR_TARGETS[1]), z.literal(PASUR_TARGETS[2])])
    .optional(),
  /**
   * مار پله ONLY (round 2) — the LEVEL (آسان / متوسط / سخت): which set of maps the board is drawn
   * from. OPTIONAL with NO default (a default would put the key on every game's payload) and judged
   * by the TRUSTED room game in `TableController.normalizeSettings`: STRIPPED for every other game;
   * on a PRIVATE ladders table DEFAULTED to `medium` when absent (so the table publishes what it
   * plays); on a PUBLIC one FORCED to `medium` (the operator: «Public tables are always متوسط»),
   * whichever way a crafted client asks. It reaches the engine as the `level` rule.
   */
  laddersLevel: z.enum(LADDERS_LEVEL_IDS).optional(),
  /**
   * مار پله ONLY (round 2) — the LOOK: `jungle`, `bg2` (the second picture) or `classic`
   * («اورجینال», no picture). Same judging as {@link laddersLevel}: stripped elsewhere; a PRIVATE
   * table DEFAULTS it to `jungle` and CLAMPS an id this build cannot draw (`LADDERS_LIVE_STYLES`)
   * to that default; a PUBLIC table STRIPS it — the ENGINE draws one of the live picture looks
   * from the seeded RNG, so every seat agrees (the view's `style`). It reaches the engine as the
   * `style` rule.
   */
  laddersStyle: z.enum(LADDERS_STYLE_IDS).optional(),
  muted: z.boolean().default(false),
  chatAllowed: z.boolean().default(true),
  deck: z.enum(CARD_DECK_IDS).default(DEFAULT_CARD_DECK),
  chess: chessSettingsSchema.optional(),
  backgammon: backgammonSettingsSchema.optional(),
});
export type ValidTableSettings = z.infer<typeof tableSettingsSchema>;

/**
 * The host's START INTENT, carried by the pre-start `start` message. The waiting
 * room offers TWO different starts and the server must not have to GUESS which
 * one was pressed:
 *  - «با ربات‌ها شروع کن» (the table is not full) ⇒ `{ withBots: true }`: the empty
 *    seats become bots.
 *  - «بزن بریم!» / «تأیید و شروع بازی» (a full HUMAN table) ⇒ no intent.
 *
 * Guessing from the roster (`humanSeats === seats`) is wrong in the real race
 * where the last seat fills between the `lobby` frame the host's client rendered
 * and their tap.
 *
 * NOTE: on a PARTNERSHIP table (hokm/shelem) the intent no longer decides whether
 * the team stage runs — the ROSTER does. As long as a second human is seated, the
 * host arranges the teams first, bots or not: with 2 humans + 2 bots «is my friend
 * my partner (یار) or my opponent (حریف)?» is a real question, and the seat parity
 * that answers it is the host's to pick.
 *
 * It arrives straight off the wire, so the server treats it as UNTRUSTED and
 * parses it (`wantsBotStart`) rather than casting.
 */
export interface StartIntent {
  /** The host pressed the bot-fill start ⇒ empty seats become bots. */
  withBots?: boolean;
}

/**
 * WHAT BECAME OF A TABLE — so a stale invite link can say something TRUE.
 *
 * Tapping an old invite lands on `joinById`, and Colyseus tells the client apart
 * only «room not found» (disposed) from «room is locked» (full, started — OR
 * finished, since a table whose winner is still reading the summary is very much
 * still there and very much locked). Mapping those two to «میز پر است یا بازی شروع
 * شده» is wrong for the single most common case: the game is simply OVER.
 *
 * So the game-server records a table's fate under {@link tableStateKey} and the API
 * serves it back: the client asks what actually happened and says exactly that.
 */
export const TABLE_STATES = ["waiting", "playing", "finished", "closed", "expired"] as const;
export type TableState = (typeof TABLE_STATES)[number];

/** Redis key for a table's fate. Written by the game-server, read by the API. */
export function tableStateKey(roomId: string): string {
  return `table-state:${roomId}`;
}

/**
 * WHY ONE PLAYER MAY NOT COME BACK TO A TABLE — the room's own three bars, which otherwise live only
 * in its memory: the host's «اخراج», the table's idle removal, a seat-fill eviction. Written by the
 * game-server under {@link tableBarKey} the moment the bar goes up, read by the bot when that player
 * taps a shared link (card-d3, operator 2026-11-05). No key, or a value not listed here, means «no
 * bar we know of», and the room's own `onAuth` still refuses them: this only moves the news earlier.
 */
export const TABLE_BARS = ["kicked", "idle", "departed"] as const;
export type TableBar = (typeof TABLE_BARS)[number];

/** Redis key for one player's {@link TableBar} at one table. Kept as long as the table's fate. */
export function tableBarKey(roomId: string, telegramUserId: string): string {
  return `table-bar:${roomId}:${telegramUserId}`;
}

/**
 * HOW A TABLE'S MATCH ENDED — and what it recorded for each player (2026-10-08, item 3).
 *
 * {@link tableStateKey} says only THAT a table finished; a player who comes back to it afterwards
 * (a private شطرنج «خروج», the opponent claims, «ادامه بازی») deserves to be told HOW, and their own
 * result. Nothing durable said either: the reason rode only the `gameover` frame, which an absent
 * player never receives, and a `match_end` event carries none for a room-decided end.
 *
 * Written by the game-server at every terminal that RECORDS a result — the same moment and the same
 * humans as the `MatchHistory` rows — so an abandoned table (nobody at the end, nothing recorded)
 * writes nothing. Kept as long as the fate itself (`TABLE_STATE_TTL_SEC`). Read by the API's
 * `GET /tables/:roomId/status`, which hands a caller their OWN row only.
 *
 * ⚠️ A SECOND KEY, NEVER A REFORMATTED ONE — {@link tableStateKey}'s bare state string is parsed as
 * one by an existing reader (see {@link tableCountdownKey}'s note).
 */
export function tableEndKey(roomId: string): string {
  return `table-end:${roomId}`;
}

/**
 * The value under {@link tableEndKey}. `reason` is the terminal's `gameover.reason` when the ROOM
 * decided the end (`claim`, `resign`, `timeout`, `idle`, `opponents-left`), else `"end"` — the board
 * ended it. `results` is keyed by the APP user id (cuid, never a Telegram id): exactly the humans
 * the terminal recorded, each with their own row's result.
 */
export const tableEndRecordSchema = z.object({
  reason: z.string().min(1).max(64),
  results: z.record(z.string(), z.enum(MATCH_RESULTS)),
});
export type TableEndRecord = z.infer<typeof tableEndRecordSchema>;

/**
 * WHEN A PRE-START TABLE WILL DEAL — the fill countdown's deadline, as an ABSOLUTE instant.
 *
 * WHY IT EXISTS: an invite must die one second before the table it points at starts, and the API
 * that owns invites cannot see the game-server's clock. It used to be told: the inviter's client
 * read the countdown off its own lobby frame and sent the number. That put a deadline the SERVER
 * enforces in the hands of a CLIENT, and it could not survive the countdown pausing — which is
 * routine, not an edge case (on `hokm-advanced-online` the mean gap between arrivals is ~103s
 * against a 45s countdown, so "someone joins, then leaves, while an invite is outstanding" is the
 * ordinary shape of a wait). So the table publishes its own deadline and the API derives from it:
 * the invite's stored `expiresAt` becomes a CEILING, and the effective deadline is
 * `min(ceiling, endsAt − 1s)`. Nothing is client-supplied, so there is nothing to forge, and a
 * pause needs no message at all — the key simply goes away and the ceiling stands again.
 *
 * ⚠️ A SECOND KEY, NEVER A REFORMATTED ONE. {@link tableStateKey}'s value is a BARE STATE STRING
 * that an existing reader parses as one, exactly as {@link onlineLiveKey}'s is a bare number for
 * the deploy-safety monitor. This is a bare epoch-millisecond number for the same reason. Written
 * and deleted together with neither of them, on its own edges.
 *
 * ⚠️ EDGE-TRIGGERED, NOT FRAME-TRIGGERED. The lobby frame is re-broadcast every second while a
 * countdown ticks (`tickCountdown`), so publishing from there would be one Redis write per second
 * per counting table. The deadline does not move as it ticks: it is written ONCE when a countdown
 * is armed and DELETED when one is cleared.
 *
 * ABSENT means "this table has no deadline right now" — paused, never armed, or a private table
 * that has no fill countdown at all. All three mean the same thing to an invite: the ceiling
 * stands. Whether a table is past its pre-start life entirely is {@link tableStateKey}'s job.
 */
export function tableCountdownKey(roomId: string): string {
  return `table-countdown:${roomId}`;
}

/**
 * How long a published countdown deadline is kept.
 *
 * Far longer than any countdown it can describe (the public fill countdown is 45s and the
 * full-house beat shorter still), so it never expires under a
 * live table; short enough that a game-server that died mid-countdown leaves nothing behind for
 * long. A key whose deadline is already PAST is harmless either way — it reads as "this table has
 * dealt", which is exactly what it means.
 */
export const TABLE_COUNTDOWN_TTL_SEC = 5 * 60;

/**
 * HOW LONG A RETURNING PLAYER IS OFFERED A DEAD TABLE — and therefore how long its fate has to be
 * remembered. THESE ARE THE SAME NUMBER, and they live here so they cannot drift apart.
 *
 * ⚠️ THEY DID DRIFT, AND A REAL PLAYER PAID FOR IT. The «ادامه بازی» strip offered a saved online
 * game for 12 hours while the table's fate ({@link tableStateKey}) was only remembered for 6 — so
 * between the sixth and twelfth hour the strip still offered a game, the join still failed, and
 * `tableJoinReason` had no record to read: the player got the vague «این میز دیگر در دسترس نیست»
 * instead of «این بازی تمام شده است» / «میزبان این میز را بست» / «این میز منقضی شد».
 *
 * A returning player must always be told plainly what happened, so the window we remember is
 * exactly the window we still offer. Twelve hours is far longer than any live table and far
 * shorter than a persisted chess row's own week.
 */
export const ONLINE_RESUME_OFFER_MS = 12 * 60 * 60 * 1000;

/**
 * LIVE ONLINE-TABLE REPORTER (deploy-safety) — the Redis key prefix + builder.
 *
 * Each ACTIVE online room (card AND chess) writes `online-live:<roomId>` = its PRESENT human
 * count, under a short self-refreshed TTL. A safe-deploy monitor scans `online-live:*`, reads
 * each value, and counts entries ≥ 2 = online tables a deploy would interrupt; 0 = a safe
 * window. The TTL makes it self-healing — a crashed/restarted game-server's stale entries
 * expire on their own. Mirrors {@link tableStateKey}: written by the game-server, read by a
 * monitor, best-effort (no Redis ⇒ nothing is reported).
 */
export const ONLINE_LIVE_KEY_PREFIX = "online-live:";

/** Redis key a LIVE online room publishes its present human count under. */
export function onlineLiveKey(roomId: string): string {
  return `${ONLINE_LIVE_KEY_PREFIX}${roomId}`;
}

/**
 * DETAIL for one live online table — what the admin panel's «🎮 میزهای در جریان» screen renders.
 *
 * Published beside {@link onlineLiveKey} under its OWN key, deliberately NOT folded into it: that
 * key's value is a BARE NUMBER by contract (the deploy-safety monitor and `redis-cli get` both
 * parse it as one), so enriching it would break the very signal used to decide whether deploying
 * is safe. Two keys, same room, same TTL, written and deleted together.
 */
export interface OnlineTableReport {
  /** Lowercase `GameId` (`"hokm"`, `"chess"`, …). */
  game: string;
  /** Humans holding a seat. NOTE this includes a player who has DROPPED but whose seat is still
   *  reclaimable — 120s in-game and THIRTY MINUTES pre-start — so an abandoned waiting table keeps
   *  looking occupied for half an hour. Use {@link OnlineTableReport.connected} for "really there". */
  humans: number;
  /** Of those seats, how many belong to a player with a LIVE session right now. */
  connected: number;
  bots: number;
  /** An invite-only table (`<game>-private`) rather than public quick-match. */
  private: boolean;
  /** Still filling / arranging — the match has not been dealt yet. */
  waiting: boolean;
  /** Epoch SECONDS the match began; null while `waiting` (there is no duration to show yet). */
  since: number | null;
  /**
   * Epoch SECONDS the ROOM was created — which is not {@link since}: a table that is still filling
   * has no match start at all, and one that dealt an hour after it opened has two very different
   * times. The admin's live-tables list is ordered by this, newest first, so a table that has just
   * appeared is the one at the top.
   *
   * OPTIONAL because the bot and the game-server deploy independently: during that window the bot
   * reads payloads written by a game-server that predates this field, and a missing creation time
   * must degrade the ORDER, never blank the screen.
   */
  createdAt?: number;
  /**
   * HOW LONG THIS MATCH IS, in the unit the game counts in — حکم ROUNDS (3/5/7) today.
   *
   * Present whenever the table actually has a chosen length: every public حکم table (its room type
   * IS the length) and a private حکم table whose host picked one. Absent for every other game, so
   * the admin card simply omits the line rather than inventing a number.
   *
   * OPTIONAL for the same reason as {@link createdAt}: the bot and the game-server deploy
   * independently, and during that window the bot reads payloads written by a game-server that
   * predates this field. A missing length must cost the LINE, never the row.
   *
   * تخته نرد's own length rides this SAME field (its `backgammon.matchLength`, 1/3/5/7). The two
   * count different things — حکم rounds vs backgammon points — but Persian names both «دست», and
   * `game` disambiguates the unit for anyone who needs to, so one field is honest and a second one
   * would only be a second thing to forget to publish.
   */
  matchLength?: number;
  /**
   * حکم ONLY — does this table offer the three SUIT-LESS calls (سرس / نرس / آس نرس)?
   *
   * What it is FOR: telling a plain «اتاق عمومی» table from an «اتاق عمومی پیشرفته» one on the
   * admin panel's «🎮 میزهای در جریان» screen. Both are public حکم tables of the same length and
   * the same size, so without this they are indistinguishable — and the advanced room's whole
   * reason to exist is the rule it plays under.
   *
   * Present on every حکم table (public AND private, either way) and absent everywhere else, so
   * another game's card simply omits the line rather than asserting something about it.
   *
   * OPTIONAL for the same reason as {@link matchLength} and {@link createdAt}: the bot and the
   * game-server deploy independently, so during that window the bot reads payloads written by a
   * game-server that predates this field. A missing value must cost the LINE, never the row.
   */
  hokmModes?: boolean;
  /**
   * حکم ONLY — does a 7-0 sweep under a SUIT-LESS call pay کوت (2 points) as a suit does?
   *
   * A per-table host setting (`hokmModeKot`), forced OFF on every public table, so it is only ever
   * a real choice on a private one. Published for the same reason as {@link hokmModes}: an invited
   * friend is being told what game they are walking into, and a non-conventional scoring rule is
   * exactly the kind of thing to say BEFORE they sit down rather than after they lose to it.
   *
   * ⚠️ حکم ONLY. تخته نرد has no کوت of any kind — its مارس (2) / سگ‌مارس (3) are RULES, identical
   * on every table, not a setting — so a backgammon table must never carry this field.
   *
   * OPTIONAL for the same deploy-skew reason as its siblings: a missing value costs the LINE.
   */
  hokmModeKot?: boolean;
  /**
   * HOW MANY SEATS this table has — the one setting منچ lets a host choose (2 / 3 / 4), and پاسور's
   * room size (2 at `pasur2-*`, 4 at `pasur-*`).
   *
   * Published for every game (it is a property of the room type, always known), but only those two
   * have anything to say with it: every other game's seat count is fixed by the game itself, so
   * naming it would be telling someone that حکم has four players.
   */
  seats?: number;
  /**
   * شطرنج ONLY — the table's time control (`"10+0"`, `"none"`, …), verbatim from its settings.
   *
   * Rendered by its own game's vocabulary («آزاد» for none), never interpreted here. Absent for
   * every other game, and for a game-server predating the field.
   */
  chessTimeControl?: string;
  /**
   * The HOST's display name on a PRIVATE table — «{name} برات جا نگه داشته».
   *
   * ⚠️ WHY A NAME IS SAFE HERE: it is the SAME name already shown to every player at the table (the
   * seat label), already sent in the invite PVs the api writes, and already resolved through the one
   * `displayNameOf` priority — nickname → Telegram first+last → app handle. No Telegram id travels
   * with it (the host's APP id rides {@link hostAppUserId}, for the api alone). It exists because a
   * table LINK carries only `t_<game>_<roomId>`: the bot has no other way to know whose table it is,
   * and «a friend saved you a seat» is the entire content of a private invitation.
   *
   * Absent on PUBLIC tables — they have no host at all (`hostId` is null by design) — and absent
   * once every human has left, so the caption falls back to naming nobody rather than a stale host.
   */
  hostName?: string;
  /**
   * The HOST's APP user id (cuid) on a PRIVATE table — the api's only server-side answer to «is the
   * caller of `POST /me/table/prepared` this table's host?», which picks the share card's line: only
   * the host is told «دوستانت را به میز خصوصی‌ات دعوت کن» («…YOUR private table»), every other sharer
   * gets the same sentence without the possessive (card-a5 item 14). The crown lives in the
   * game-server's memory; this is how the decision stays the server's rather than the client's.
   *
   * ⚠️ AN APP CUID, NEVER A TELEGRAM ID — the same leak-free public id every seat's lobby frame
   * already carries (`LobbyPlayer.user.userId`), so it tells no reader anything a player at the
   * table is not already sent. No route returns a report, and the bot's captions never read it.
   * (Named `hostAppUserId` because the game-server's rematch option `hostUserId` is the host's
   * Telegram routing id — two ids, two names.)
   *
   * Present when the host is a human whose identity resolved at auth; absent on a PUBLIC table, once
   * nobody holds the crown, and from a game-server that predates the field. EVERY absence reads as
   * «not the host» — the neutral line — never as the host: the possessive is said only on proof.
   */
  hostAppUserId?: string;
}

/**
 * WRITER-LIVENESS HEARTBEAT for the live-table keyspace.
 *
 * An empty `online-live:*` scan is AMBIGUOUS: either nobody is playing, or the game-server that
 * writes those keys is down / not deployed. Rendering it as a confident «۰ میز آنلاین» is the same
 * class of mistake as counting a keyspace nothing writes yet — it reads as "nobody is playing"
 * when the truth is "we cannot see". The game-server stamps this key on a fixed cadence REGARDLESS
 * of how many tables exist, so a reader can tell the two apart: heartbeat present + no tables = a
 * genuine zero; no heartbeat = UNKNOWN.
 *
 * Deliberately NOT under the `online-live:` prefix — a `online-live:*` scan must not count it.
 */
export const ONLINE_LIVE_HEARTBEAT_KEY = "online-live-heartbeat";

export const ONLINE_TABLE_KEY_PREFIX = "online-table:";

/** Redis key a LIVE online room publishes its {@link OnlineTableReport} under. */
export function onlineTableKey(roomId: string): string {
  return `${ONLINE_TABLE_KEY_PREFIX}${roomId}`;
}

/**
 * `unknown` = we have no record (a very old table — the key has expired).
 *
 * `end` (contract C2, 2026-10-08) — HOW a finished table's match ended, from {@link tableEndKey}:
 * `reason` is the room's reason (`claim`, `resign`, `timeout`, …) or `"end"`, and `result` is the
 * CALLER's own recorded result, present only if they were one of the humans it recorded. Absent
 * whenever there is nothing to say — no record (an abandoned table, an older game-server, a Redis
 * blip) or a table that is not `finished` — so an older client, and an older api, read as before.
 */
export const tableStatusResponseSchema = z.object({
  state: z.enum([...TABLE_STATES, "unknown"]),
  end: z
    .object({
      reason: z.string(),
      result: z.enum(MATCH_RESULTS).optional(),
    })
    .optional(),
});
export type TableStatusResponse = z.infer<typeof tableStatusResponseSchema>;

/**
 * `ChatRoom.onAuth`'s refusal when the joiner is not seated at the table — a CROSS-SERVICE
 * CONTRACT, thrown by the game-server and matched by the web.
 *
 * Table-chat membership is registered by the game room and cleared when it disposes, so this
 * refusal means THE TABLE IS GONE. It is terminal: no amount of retrying brings a disposed table
 * back, and the client used to retry it for the full 135s window and then file an ERROR beacon —
 * ~90 refusals and 30 retry bursts in 9 hours of normal table churn.
 *
 * Matched on the MESSAGE rather than the numeric code because `4216 APPLICATION_ERROR` is what
 * `ChatRoom.onAuth` throws for every reason including ones that ARE transient (a graph lookup
 * failing on a DB blip), and killing chat for those would be worse than the retries.
 * `packages/shared/test/deploy-safety.test.ts` pins the game-server literal against this constant
 * so a server-side rewording fails the build rather than silently restoring the retry storm.
 */
export const TABLE_CHAT_NOT_A_MEMBER = "Not a member of this table";
